import { DeleteItem } from "@/components/DeleteItem";
import { ResourceHeader } from "@/components/ui/ResourceHeader";
import { TabItem } from "@/components/ui/TabNavigation/types";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useEditPromptTemplate } from "@/queries";
import { getTemplateFormat } from "@/utils/utils";
import {
  ChartBarIcon,
  PencilIcon as PencilIconOutline,
  TrashIcon,
  ViewListIcon,
  BookOpenIcon,
} from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarIconSolid,
  PencilIcon as PencilIconSolid,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import EvaluationHistory from "../EvaluationHistory";
import Breadcrumbs from "./Breadcrumbs";
import EditorContent from "./EditorContent";
import {
  usePromptDeletion,
  usePromptTemplateData,
  useTabNavigation,
} from "./hooks";
import { useEvaluationsTableColumns } from "./hooks/useEvaluationsTableColumns";
import OverviewContent from "./OverviewContent";
import RelatedContentDialog from "./RelatedContentDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import CodeEditor from "@/components/CodeEditor";
import { LanguageType } from "@/components/CodeEditor/types";
import { languages } from "@/components/CodeEditor/constants";

const PromptTemplate = () => {
  const { promptId, versionId } = useParams();
  const columns = useEvaluationsTableColumns();
  const { promptVersion, promptRegistry, totalVersions, promptName } =
    usePromptTemplateData(promptId!, versionId);

  const overviewSessionId = useRef(v4());
  const navigate = useNavigate();
  const [isDeploymentGuideModalOpen, setDeploymentGuideModalOpen] =
    useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageType>(
    languages[0],
  );

  useEffect(() => {
    overviewSessionId.current = v4();
  }, [versionId]);

  const { getCurrentTabIndex, handleTabChange, showVersions, setShowVersions } =
    useTabNavigation(promptId!, versionId!);
  const { isDeleteDialogOpen, setIsDeleteDialogOpen, handleDeletePrompt } =
    usePromptDeletion(promptId!);

  const editPromptTemplate = useEditPromptTemplate();
  const { activeWorkspaceId } = useUser();
  const auth = useAuth();
  const userToken = auth?.userToken!;

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        id: 0,
        name: "Overview",
        outlineIcon: (
          <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        solidIcon: (
          <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        content: (
          <OverviewContent
            promptVersion={promptVersion}
            showVersions={showVersions}
            setShowVersions={setShowVersions}
            totalVersions={totalVersions}
            navigateToPlayground={() =>
              navigate(handleTabChange(1) + overviewSessionId.current)
            }
            getCurrentTabIndex={getCurrentTabIndex}
          />
        ),
        path: handleTabChange(0),
      },
      {
        id: 1,
        name: "Editor",
        outlineIcon: (
          <PencilIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        solidIcon: (
          <PencilIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        content: (
          <EditorContent
            isLoading={promptVersion.isLoading}
            instanceId={overviewSessionId.current}
            promptVersion={promptVersion.data}
          />
        ),
        path: handleTabChange(1) + overviewSessionId.current,
        className: "relative",
      },
      {
        id: 2,
        name: "Evaluations",
        outlineIcon: (
          <ChartBarIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        solidIcon: (
          <ChartBarIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        content: (
          <EvaluationHistory tableProps={{ columns }} showViewButtons={false} />
        ),
        path: handleTabChange(2),
      },
    ],
    [
      columns,
      promptVersion,
      showVersions,
      setShowVersions,
      totalVersions,
      getCurrentTabIndex,
      handleTabChange,
      navigate,
    ],
  );

  const handleTitleEdit = useCallback(
    ({ newTitle }: { newTitle: string }) => {
      if (!promptRegistry.data) return;
      editPromptTemplate.mutate({
        prompt_id: Number(promptId),
        userToken,
        basePromptTemplate: {
          prompt_name: newTitle,
          tags: promptRegistry.data.tags,
          folder_id: promptRegistry.data.folder_id!,
          workspace_id: activeWorkspaceId!,
        },
        activeWorkspaceId: activeWorkspaceId!,
      });
    },
    [
      promptRegistry.data,
      promptId,
      userToken,
      activeWorkspaceId,
      editPromptTemplate,
    ],
  );

  const handleTagsEdit = useCallback(
    (tags: string[]) => {
      if (!promptRegistry.data) return;
      editPromptTemplate.mutate({
        prompt_id: Number(promptId),
        userToken,
        basePromptTemplate: {
          prompt_name: promptRegistry.data.prompt_name,
          tags,
          folder_id: promptRegistry.data.folder_id!,
          workspace_id: activeWorkspaceId!,
        },
        activeWorkspaceId: activeWorkspaceId!,
      });
    },
    [
      promptRegistry.data,
      promptId,
      userToken,
      activeWorkspaceId,
      editPromptTemplate,
    ],
  );

  const handleDeploymentGuideClick = useCallback(() => {
    setDeploymentGuideModalOpen(true);
  }, []);

  // Get input variables from the prompt template
  const inputVariables =
    promptVersion.data?.prompt_template.input_variables ||
    promptVersion.data?.rendered_template?.input_variables ||
    [];

  // Get the most recent label if available
  const mostRecentLabel =
    promptVersion.data?.labels?.length > 0
      ? promptVersion.data.labels[0]?.name
      : null;

  // Generate input variables object for code examples
  const generateInputVariablesObject = (language: "python" | "javascript") => {
    if (inputVariables.length === 0) {
      return language === "python"
        ? '{\n        "topic": "The American Revolution"\n    }'
        : '{\n        topic: "The American Revolution"\n    }';
    }

    const sampleValues = inputVariables.map((variable) => {
      const sampleValue = variable.includes("message")
        ? "Hello, how can you help me?"
        : variable.includes("history")
        ? "Previous conversation context"
        : variable.includes("progress")
        ? "Task in progress"
        : `Sample value for ${variable}`;

      return language === "python"
        ? `        "${variable}": "${sampleValue}"`
        : `        ${variable}: "${sampleValue}"`;
    });

    return language === "python"
      ? `{\n${sampleValues.join(",\n")}\n    }`
      : `{\n${sampleValues.join(",\n")}\n    }`;
  };

  return (
    <div className="flex h-full flex-col p-1 pb-4">
      <div className="mb-3">
        <Breadcrumbs />
      </div>
      <ResourceHeader
        showVersions={showVersions}
        onToggleVersions={() => setShowVersions(!showVersions)}
        isLoading={promptRegistry.isLoading}
        title={promptName || ""}
        minimal={true}
        onTitleEdit={handleTitleEdit}
        id={promptId!}
        format={
          (promptVersion.data &&
            getTemplateFormat(promptVersion.data.prompt_template)) ||
          "f-string"
        }
        tags={promptRegistry.data?.tags || []}
        onTagsEdit={handleTagsEdit}
        createdAt={promptRegistry.data?.created_at || ""}
        actions={[
          {
            label: "Delete",
            onClick: () => setIsDeleteDialogOpen(true),
            variant: "destructiveLink",
            icon: <TrashIcon className="mr-2 h-4 w-4" />,
          },
        ]}
        tabsProps={{
          tabs: tabs,
          currentTabIndex: getCurrentTabIndex(),
          handleTabChange: handleTabChange,
          actionButtons: (
            <div className="relative bottom-1 flex gap-x-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700"
                onClick={handleDeploymentGuideClick}
              >
                <BookOpenIcon className="h-4 w-4" />
                <span>Deployment Guide</span>
              </Button>
              <RelatedContentDialog />
            </div>
          ),
        }}
      />

      {promptRegistry.data && (
        <DeleteItem
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          setDelete={() => handleDeletePrompt()}
          item={promptRegistry.data?.prompt_name}
          itemType="template"
        />
      )}

      <Dialog
        open={isDeploymentGuideModalOpen}
        onOpenChange={setDeploymentGuideModalOpen}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Deploying a Prompt</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <p className="text-gray-600">
                When you deploy a prompt in PromptLayer, you publish the
                template so your app can pull it at runtime—asking for the
                latest version, a specific version number, or a label like
                "prod" or "staging", and automatically send the request +
                response back to PromptLayer for full tracking. This keeps copy
                tweaks and experimentation out of code releases while giving
                your team built-in version history, usage analytics, and
                debugging traces.
              </p>
            </div>

            <div className="space-y-4">
              <div className="max-w-full">
                <CodeEditor
                  languages={languages}
                  selectedLanguage={selectedLanguage}
                  handleLanguageChange={setSelectedLanguage}
                  controlsType="tabs"
                  code={
                    selectedLanguage === "python"
                      ? `from promptlayer import PromptLayer

# Initialize PromptLayer client
pl_client = PromptLayer(api_key="your_api_key")

# Run the "${promptName || "your-prompt"}" prompt template
response = pl_client.run(
    prompt_name="${promptName || "your-prompt"}",
    input_variables=${generateInputVariablesObject("python")},${
      mostRecentLabel ? `\n    prompt_release_label="${mostRecentLabel}",` : ""
    }
    tags=["production", "tutorial"],
    metadata={"user_id": "abc123"}
)`
                      : `import { PromptLayer } from "promptlayer";

// Initialize PromptLayer client
const pl_client = new PromptLayer({ apiKey: "your_api_key" });

// Run the "${promptName || "your-prompt"}" prompt template
const response = await pl_client.run({
    promptName: "${promptName || "your-prompt"}",
    inputVariables: ${generateInputVariablesObject("javascript")},${
      mostRecentLabel ? `\n    promptReleaseLabel: "${mostRecentLabel}",` : ""
    }
    tags: ["production", "tutorial"],
    metadata: { userId: "abc123" }
});`
                  }
                  showRuntimeDetails={false}
                  height="240px"
                  editorOptions={{
                    fontSize: 12,
                    lineHeight: 18,
                    padding: { top: 12, bottom: 12 },
                    renderLineHighlight: "none",
                    readOnly: true,
                  }}
                />
              </div>
              <p className="text-sm text-gray-600">
                Looking to set up caching and redundancy?{" "}
                <a
                  href="https://docs.promptlayer.com/onboarding-guides/deployment-strategies#deployment-strategies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  View deployment strategies
                </a>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                window.open(
                  "https://docs.promptlayer.com/features/prompt-registry",
                  "_blank",
                )
              }
            >
              Go to Docs
            </Button>
            <Button onClick={() => setDeploymentGuideModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromptTemplate;
